import Box from 'components/shared/Box'
import Grid from 'components/shared/Grid'
import Info from 'components/shared/Info'
import Link from 'components/shared/Link'
import Modal from 'components/shared/Modal'
import Text from 'components/shared/Text'
import { SPA_CONTRAINDICATIONS_DOC } from 'constants/links'
import { formatPrice } from 'helpers/helpers'
import { FormattedMessage, useIntl } from 'react-intl'
import SpaLoader from 'routes/Upsell/SpaCreator/SpaLoader'
import { useGetCreatorSpaTreatmentGroupsQuery } from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import TreatmentSlider from './subcomponents/TreatmentSlider/TreatementSlider'
import {
    ContentWrapper,
    ModalBodyWrapper,
    StyledTreatmentDetailsModal,
    VariantWrapper,
} from './TreatmentDetailsModal.styles'

interface TreatmentDetailsModalProps {
    treatmentId?: string
    isOpened: boolean
    closeModal: () => void
}

const TreatmentDetailsModal = ({
    treatmentId,
    isOpened,
    closeModal,
}: TreatmentDetailsModalProps) => {
    const { formatMessage } = useIntl()
    const { language } = useApp()

    const { isError, data, isFetching } = useGetCreatorSpaTreatmentGroupsQuery(
        {
            _locale: language,
            spaTreatmentGroupId: treatmentId || '',
        },
        {
            skip: !treatmentId || !isOpened,
        }
    )

    const maxVariantsColumns = 3

    return (
        <StyledTreatmentDetailsModal>
            <Modal
                isOpened={isOpened}
                maxWidth={670}
                onClose={closeModal}
                isMobileFullscreen
                hasGlobalHeader
                headerMessage={formatMessage({
                    id: 'upsells.wellness.treatment_details',
                    defaultMessage: 'Szczegóły zabiegu',
                })}
                testIdPrefix="treatment-details-modal"
            >
                <ModalBodyWrapper gap={8}>
                    {!data ? (
                        <>
                            {isFetching && <SpaLoader height={[200, 540]} />}
                            {isError && (
                                <Box
                                    minHeight={540}
                                    align="center"
                                    justify="center"
                                    isFullWidth
                                >
                                    <Box width={['100%', 380]}>
                                        <Info
                                            color="info_warning"
                                            variantType="error"
                                            align="center"
                                            isFullWidthContent
                                            hasNoBackground
                                        >
                                            <Text
                                                variant="bodySmall"
                                                color="grayscale_900"
                                            >
                                                <FormattedMessage
                                                    id="global.error.generic_technical_issue"
                                                    defaultMessage="Napotkaliśmy problem techniczny. Odśwież stronę lub spróbuj ponownie później."
                                                />
                                            </Text>
                                        </Info>
                                    </Box>
                                </Box>
                            )}
                        </>
                    ) : (
                        <ContentWrapper gap={[24, 40]}>
                            {/* gallery */}
                            {!!data.imageGalleryUrls && (
                                <TreatmentSlider
                                    images={data.imageGalleryUrls}
                                    alt=""
                                />
                            )}
                            <Box gap={12}>
                                {/* name */}
                                {!!data.name && (
                                    <Text
                                        variant="heading4"
                                        color="grayscale_900"
                                    >
                                        {data.name}
                                    </Text>
                                )}
                                {/* TODO: if there is time, do it in a better way */}
                                {/* description */}
                                {!!data.longDescription && (
                                    <Text
                                        variant="body"
                                        color="grayscale_900"
                                        dangerouslySetInnerHTML={{
                                            __html: data.longDescription
                                                .split('\\n')
                                                .join('<br/><br/>'),
                                        }}
                                    />
                                )}
                            </Box>
                            {/* available variants */}
                            {!!data.variants && (
                                <Box gap={12} isFullWidth>
                                    <Text
                                        variant="heading5"
                                        color="grayscale_900"
                                    >
                                        {data.variants.length > 1 ? (
                                            <FormattedMessage
                                                id="upsells.wellness.available_in_variants"
                                                defaultMessage="Dostępny w {variants} wariantach:"
                                                values={{
                                                    variants:
                                                        data.variants.length,
                                                }}
                                            />
                                        ) : (
                                            <FormattedMessage
                                                id="upsells.wellness.variant_price"
                                                defaultMessage="Cena:"
                                            />
                                        )}
                                    </Text>
                                    <Grid
                                        columns={
                                            data.variants.length >
                                            maxVariantsColumns
                                                ? maxVariantsColumns
                                                : data.variants.length
                                        }
                                        gap={[8, 16]}
                                    >
                                        {data.variants.map(
                                            ({ duration, price }) => (
                                                <VariantWrapper
                                                    key={duration}
                                                    gap={[2, 4]}
                                                >
                                                    {!!duration && (
                                                        <Text
                                                            variant="body"
                                                            color="grayscale_900"
                                                        >
                                                            {duration} min
                                                        </Text>
                                                    )}
                                                    {price !== undefined && (
                                                        <Box
                                                            gap={4}
                                                            direction="row"
                                                            align="baseline"
                                                        >
                                                            <Text
                                                                variant="heading4"
                                                                color="secondary_500"
                                                            >
                                                                {formatPrice(
                                                                    price
                                                                )}
                                                            </Text>
                                                            <Text
                                                                variant="bodyExtraSmallStrong"
                                                                color="secondary_500"
                                                            >
                                                                PLN
                                                            </Text>
                                                        </Box>
                                                    )}
                                                </VariantWrapper>
                                            )
                                        )}
                                    </Grid>
                                </Box>
                            )}
                            {/* contraindications */}
                            <Box gap={16}>
                                <Text variant="heading5" color="grayscale_900">
                                    <FormattedMessage
                                        id="upsells.wellness.treatment_contraindications"
                                        defaultMessage="Przeciwskazania do zabiegów"
                                    />
                                </Text>
                                <Text variant="body" color="grayscale_900">
                                    <FormattedMessage
                                        id="upsells.wellness.treatment_contraindications_details"
                                        defaultMessage="Przed rezerwacją zapoznaj się z przeciwwskazaniami. W przypadku wykrycia ich w trakcie wizyty, nasi specjaliści, w miarę możliwości, zaproponują zabieg alternatywny."
                                    />
                                </Text>
                                <Link
                                    path={SPA_CONTRAINDICATIONS_DOC}
                                    variant="secondary"
                                    textVariant="body16Strong"
                                    icon={null}
                                    target="_blank"
                                    data-testid="treatment-details-modal-link-contraindications"
                                >
                                    <FormattedMessage
                                        id="upsells.wellness.treatment_contraindications_link"
                                        defaultMessage="Zobacz listę przeciwwskazań"
                                    />
                                </Link>
                            </Box>
                        </ContentWrapper>
                    )}
                </ModalBodyWrapper>
            </Modal>
        </StyledTreatmentDetailsModal>
    )
}

export default TreatmentDetailsModal
